import React, { Fragment, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { appVersion, productAuthor } from 'config';
import { useAuth } from 'context/AuthContext';
import useUrlSearchParams from 'hooks/useUrlSearchParams';
import Loading from 'components/Loading';
import FormLogin from 'components/FormLogin';
import FormOneTimePassword from 'components/FormOneTimePassword';
import LogoVerticalWhite from 'assets/custom/hiperdino/logo-vertical-white.png';
import LogoVertical from 'assets/custom/hiperdino/logo-vertical.png';

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Welcome = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 100px;
  color: ${props => props.theme.colors.white};
  background: linear-gradient(-20deg, #65b32e, #00633a);
`;

const WelcomeLogo = styled.div`
  width: 450px;
`;

const WelcomeLogoImg = styled.img`
  width: 100%;
`;

const Sidebar = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 400px;
`;

const SidebarContent = styled.div`
  margin: auto;
  padding: 16px 40px;
`;

const SidebarLogo = styled.div`
  width: 240px;
  margin: auto;
`;

const SidebarLogoImg = styled.img`
  width: 100%;
`;

const FormHeader = styled.div`
  margin: 20px 0 40px 0;
  padding: 0 20px;
  text-align: center;
  font-size: 14px;
  color: #bebebe;
`;

const Copyright = styled.footer`
  flex: none;
  margin-bottom: 10px;
  text-align: center;
  font-size: 12px;
  color: #828282;
`;

const Login = props => {
  const { searchParams, removeSearchParams } = useUrlSearchParams();
  const { ssotoken } = searchParams;

  const { login, loginWithUsernameAndPassword, loginWithOneTimePassword } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [otpFields, setOtpFields] = useState(null);

  const doLogin = useCallback(
    ({ loginFn, loginData, onSuccess = removeSearchParams }) => {
      setLoading(true);
      loginFn(loginData)
        .then(onSuccess)
        .catch(error => {
          const { status, data } = error;
          setLoading(false);
          setError(data?.message || 'No se ha podido establecer una conexión con el servidor');
          if (status === 400) return setOtpFields(loginData);
        });
    },
    [removeSearchParams],
  );

  // Hace login si existe `ssotoken` en los search params
  useEffect(() => {
    const handleLoginWithSsotoken = ({ ssotoken }) => {
      const data = { ssotoken };
      doLogin({ loginFn: login, loginData: { data } });
    };
    if (ssotoken) return handleLoginWithSsotoken({ ssotoken });
  }, [doLogin, login, ssotoken]);

  const handleLoginWithUsernameAndPassword = data => {
    doLogin({ loginFn: loginWithUsernameAndPassword, loginData: data });
  };

  const handleLoginWithOneTimePassword = data => {
    doLogin({ loginFn: loginWithOneTimePassword, loginData: data });
  };

  return (
    <Wrapper>
      <Welcome>
        <WelcomeLogo>
          <WelcomeLogoImg src={LogoVerticalWhite} />
        </WelcomeLogo>
      </Welcome>
      <Sidebar>
        {loading && <Loading />}
        <SidebarContent>
          <SidebarLogo>
            <SidebarLogoImg src={LogoVertical} />
          </SidebarLogo>
          {otpFields ? (
            <Fragment>
              <FormHeader>
                Su contraseña es de un solo uso y debe cambiarla para poder acceder a la plataforma
              </FormHeader>
              <FormOneTimePassword
                username={otpFields.username}
                password={otpFields.password}
                onSubmit={handleLoginWithOneTimePassword}
                feedback={error}
              />
            </Fragment>
          ) : (
            <Fragment>
              <FormHeader>
                Introduce tu usuario y contraseña para acceder a la plataforma
              </FormHeader>
              <FormLogin onSubmit={handleLoginWithUsernameAndPassword} feedback={error} />
            </Fragment>
          )}
        </SidebarContent>
        <Copyright>
          © {productAuthor}. v{appVersion}
        </Copyright>
      </Sidebar>
    </Wrapper>
  );
};

Login.propTypes = {};

Login.defaultProps = {};

export default Login;
